/**
 * CloudOps API service for FinOps cost management
 */

import { demoModeManager } from './demoModeManager';
import { getCloudOpsMockData } from './mockData';

const PLATFORMOPS_API_BASE = process.env.REACT_APP_PLATFORMOPS_API_URL;

/**
 * Get the selected client ID from localStorage (set by ClientContext)
 * Used by partner/aws/admin users who need to specify which client they're viewing
 */
const getSelectedClientId = (): string | null => {
  try {
    return localStorage.getItem('selectedClientId');
  } catch {
    return null;
  }
};

const getSelectedProjectId = (): string | null => {
  try {
    return localStorage.getItem('selectedProjectId');
  } catch {
    return null;
  }
};

interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
}

interface FinOpsMetricsParams {
  clientId?: string;
  timePeriod?: string;
  startDate?: string;
  endDate?: string;
}

interface ServiceBreakdownParams {
  clientId?: string;
  timePeriod?: string;
  accountId?: string;
  startDate?: string;
  endDate?: string;
}

interface MonitoringEnvironmentsParams {
  clientId?: string;
  isActive?: boolean;
}

interface UptimeTrendsParams {
  clientId?: string;
  days?: number;
}

interface ManualCreditsParams {
  client_id: string;
  aws_account_id?: string;
  is_active?: boolean;
  include_expired?: boolean;
}

interface PlatformOverviewParams {
  client_id?: string;
}

interface CostAnomaliesParams {
  clientId?: string;
  severityFilter?: string;
  startDate?: string;
  endDate?: string;
  environmentId?: string;
}

interface CostOptimizationFilters {
  status?: string;
  type?: string;
}

interface RecommendationPatch {
  status?: string;
  project_id?: string | null;
  reasoning?: string;
  meta_data?: Record<string, unknown>;
}

interface GuardrailsViolationPatch {
  status?: string;
  project_id?: string | null;
  assigned_to?: string | null;
  resolution_notes?: string | null;
}

/**
 * Make authenticated API call to cloudops service
 */
export async function cloudopsApiCall(endpoint: string, options: RequestOptions = {}): Promise<any> {
  let finalEndpoint = endpoint;

  // Apply global filter
  let filterApplied = false;
  try {
    const stored = localStorage.getItem('globalFilter');
    if (stored) {
      const { filterType, selectedId } = JSON.parse(stored);
      if (selectedId) {
        const separator = finalEndpoint.includes('?') ? '&' : '?';
        if (filterType === 'project') {
          finalEndpoint = `${finalEndpoint}${separator}project_id=${selectedId}`;
          filterApplied = true;
        } else if (filterType === 'workload') {
          finalEndpoint = `${finalEndpoint}${separator}workload_id=${selectedId}`;
          filterApplied = true;
        } else if (filterType === 'account') {
          finalEndpoint = `${finalEndpoint}${separator}account_id=${selectedId}`;
          filterApplied = true;
        } else if (filterType === 'environment') {
          finalEndpoint = `${finalEndpoint}${separator}environment_id=${selectedId}`;
          filterApplied = true;
        }
      }
    }
  } catch {
    // ignore
  }

  // Backward compat
  if (!filterApplied) {
    const projectId = getSelectedProjectId();
    if (projectId) {
      const separator = finalEndpoint.includes('?') ? '&' : '?';
      finalEndpoint = `${finalEndpoint}${separator}project_id=${projectId}`;
    }
  }

  // Check if demo mode is active
  const { isDemoMode } = demoModeManager.getDemoMode();

  if (isDemoMode) {
    const mockResult = getCloudOpsMockData(finalEndpoint);
    return mockResult;
  }

  const token = localStorage.getItem('token');

  const response = await fetch(`${PLATFORMOPS_API_BASE}${finalEndpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

/**
 * Get budget and cost metrics
 */
export async function getFinOpsMetrics({ clientId, timePeriod = 'current_month', startDate, endDate }: FinOpsMetricsParams = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  params.append('time_period', timePeriod);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  return cloudopsApiCall(`/finops/metrics?${params}`);
}

/**
 * Get cost breakdown by AWS service
 */
export async function getServiceBreakdown({ clientId, timePeriod = 'current_month', accountId, startDate, endDate }: ServiceBreakdownParams = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  params.append('time_period', timePeriod);
  if (accountId) params.append('account_id', accountId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  return cloudopsApiCall(`/finops/service-breakdown?${params}`);
}

/**
 * Get burn rate trend (planned vs actual)
 */
export async function getBurnRateTrend({ clientId, timePeriod = 'current_month', accountId, startDate, endDate }: ServiceBreakdownParams = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  params.append('time_period', timePeriod);
  if (accountId) params.append('account_id', accountId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  return cloudopsApiCall(`/finops/burn-rate-trend?${params}`);
}

/**
 * Get monthly spend trend
 */
export async function getMonthlySpendTrend({ clientId, timePeriod = 'last_3_months', accountId, startDate, endDate }: ServiceBreakdownParams = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  params.append('time_period', timePeriod);
  if (accountId) params.append('account_id', accountId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  return cloudopsApiCall(`/finops/monthly-spend?${params}`);
}

/**
 * Get AI model costs
 */
export async function getAIModelCosts({ clientId, timePeriod = 'current_month', startDate, endDate }: FinOpsMetricsParams = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  params.append('time_period', timePeriod);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  return cloudopsApiCall(`/finops/ai-model-costs?${params}`);
}

/**
 * Get workload cost breakdown
 */
export async function getWorkloadCosts({ clientId, timePeriod = 'last_3_months', startDate, endDate }: FinOpsMetricsParams = {}): Promise<any> {
  const params = new URLSearchParams();
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  params.append('time_period', timePeriod);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  return cloudopsApiCall(`/finops/workload-costs?${params}`);
}

/**
 * Get budgets
 */
export async function getBudgets({ clientId }: { clientId?: string } = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);

  return cloudopsApiCall(`/finops/budgets?${params}`);
}

/**
 * Create new budget
 */
export async function createBudget(budgetData: Record<string, any>): Promise<any> {
  return cloudopsApiCall('/finops/budgets', {
    method: 'POST',
    body: JSON.stringify(budgetData),
  });
}

/**
 * Get budget alerts
 */
export async function getBudgetAlerts({ unacknowledgedOnly = true }: { unacknowledgedOnly?: boolean } = {}): Promise<any> {
  const params = new URLSearchParams();
  params.append('unacknowledged_only', String(unacknowledgedOnly));

  return cloudopsApiCall(`/finops/alerts?${params}`);
}

/**
 * Refresh cost data from AWS Cost Explorer
 */
export async function refreshCostData({ clientId }: { clientId?: string } = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);

  return cloudopsApiCall(`/finops/refresh?${params}`, {
    method: 'POST',
  });
}

/**
 * Get cost anomalies from AWS Cost Anomaly Detection
 */
export async function getCostAnomalies({ clientId, severityFilter, startDate, endDate, environmentId }: CostAnomaliesParams = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  if (severityFilter && severityFilter !== 'All') params.append('severity_filter', severityFilter.toUpperCase());
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (environmentId) params.append('environment_id', environmentId);

  return cloudopsApiCall(`/finops/anomalies?${params}`);
}

/**
 * Trigger anomaly sync from AWS Cost Anomaly Detection
 */
export async function triggerAnomalySync(): Promise<{ message: string; task_id: string }> {
  return cloudopsApiCall('/finops/anomalies/sync', { method: 'POST' });
}

/**
 * Get AWS credits breakdown by environment
 */
export async function getCreditsBreakdown({ clientId, timePeriod = 'current_month' }: { clientId?: string; timePeriod?: string } = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  params.append('time_period', timePeriod);

  return cloudopsApiCall(`/finops/credits-breakdown?${params}`);
}

/**
 * Get tagging compliance data
 */
export async function getTaggingData({ clientId }: { clientId?: string } = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);

  return cloudopsApiCall(`/tagging/data?${params}`);
}

/**
 * Create tagging policy
 */
export async function createPolicy(policyData: Record<string, any>): Promise<any> {
  return cloudopsApiCall('/tagging/policies', {
    method: 'POST',
    body: JSON.stringify(policyData),
  });
}

/**
 * Update tagging policy
 */
export async function updatePolicy(policyId: string, policyData: Record<string, any>): Promise<any> {
  return cloudopsApiCall(`/tagging/policies/${policyId}`, {
    method: 'PUT',
    body: JSON.stringify(policyData),
  });
}

/**
 * Delete tagging policy
 */
export async function deletePolicy(policyId: string): Promise<any> {
  return cloudopsApiCall(`/tagging/policies/${policyId}`, {
    method: 'DELETE',
  });
}

/**
 * ============================================================================
 * GUARDRAILS API METHODS
 * ============================================================================
 */

// Guardrails Environments

/**
 * List AWS environments for guardrails
 */
export async function getGuardrailsEnvironments(clientId?: string): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  // Prefer the explicitly provided clientId; fall back to the persisted selection.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/guardrails/environments${queryString ? '?' + queryString : ''}`);
}

/**
 * List guardrail groups (with client-scoped active status)
 */
export async function getGuardrailsGroups(clientId?: string): Promise<any> {
  const params = new URLSearchParams();
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/guardrails/groups${queryString ? '?' + queryString : ''}`);
}

/**
 * Toggle guardrail group active status for a client
 */
export async function toggleGuardrailGroup(groupId: string, isActive: boolean): Promise<any> {
  const params = new URLSearchParams();
  const effectiveClientId = getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/guardrails/groups/${groupId}${queryString ? '?' + queryString : ''}`, {
    method: 'PATCH',
    body: JSON.stringify({ is_active: isActive }),
  });
}

/**
 * Trigger manual scan for an environment
 */
export async function triggerEnvironmentScan(environmentId: string, scanType: string = 'full'): Promise<any> {
  const params = new URLSearchParams();
  params.append('scan_type', scanType);
  return cloudopsApiCall(`/guardrails/environments/${environmentId}/scan?${params}`, {
    method: 'POST',
  });
}

// Guardrails Resources

/**
 * List resources for an environment with pagination.
 * @returns {{ data: Array, meta: { page, limit, total, totalPages } }}
 */
export async function getGuardrailsResources(environmentId: string, resourceType?: string, page: number = 1, limit: number = 20): Promise<any> {
  const params = new URLSearchParams();
  if (resourceType) params.append('resource_type', resourceType);
  if (page != null) params.append('page', String(page));
  if (limit != null) params.append('limit', String(limit));
  const queryString = params.toString();
  return cloudopsApiCall(`/guardrails/environments/${environmentId}/resources${queryString ? '?' + queryString : ''}`);
}

/**
 * Get resource details
 */
export async function getGuardrailsResource(resourceId: string): Promise<any> {
  return cloudopsApiCall(`/guardrails/resources/${resourceId}`);
}

// Guardrails Policies

/**
 * List guardrail policies with pagination.
 * @returns {{ data: Array, meta: { page, limit, total, totalPages } }}
 */
export async function getGuardrailsPolicies(category?: string, resourceType?: string, page: number = 1, limit: number = 20, groupId?: string): Promise<any> {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  if (resourceType) params.append('resource_type', resourceType);
  if (groupId) params.append('group_id', groupId);
  if (page != null) params.append('page', String(page));
  if (limit != null) params.append('limit', String(limit));
  const queryString = params.toString();
  return cloudopsApiCall(`/guardrails/policies${queryString ? '?' + queryString : ''}`);
}

/**
 * Get guardrail policy details
 */
export async function getGuardrailsPolicy(policyId: string): Promise<any> {
  return cloudopsApiCall(`/guardrails/policies/${policyId}`);
}

/**
 * Create guardrail policy (Admin only)
 */
export async function createGuardrailsPolicy(policyData: Record<string, any>): Promise<any> {
  return cloudopsApiCall('/guardrails/policies', {
    method: 'POST',
    body: JSON.stringify(policyData),
  });
}

/**
 * Update guardrail policy (Admin only)
 */
export async function updateGuardrailsPolicy(policyId: string, policyData: Record<string, any>): Promise<any> {
  return cloudopsApiCall(`/guardrails/policies/${policyId}`, {
    method: 'PUT',
    body: JSON.stringify(policyData),
  });
}

/**
 * Delete guardrail policy (Admin only)
 */
export async function deleteGuardrailsPolicy(policyId: string): Promise<any> {
  return cloudopsApiCall(`/guardrails/policies/${policyId}`, {
    method: 'DELETE',
  });
}

// Violations

/**
 * List violations for an environment
 */
export async function getGuardrailsViolations(environmentId: string, status?: string, page?: number | string, limit?: number | string): Promise<any> {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (page) params.append('page', String(page));
  if (limit) params.append('limit', String(limit));
  const queryString = params.toString();
  return cloudopsApiCall(`/guardrails/environments/${environmentId}/violations${queryString ? '?' + queryString : ''}`);
}

/**
 * Get violation details
 */
export async function getGuardrailsViolation(violationId: string, clientId?: string): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  // Prefer the explicitly provided clientId; fall back to the persisted selection.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/guardrails/violations/${violationId}${queryString ? '?' + queryString : ''}`);
}

/**
 * Acknowledge violation
 */
export async function acknowledgeViolation(violationId: string): Promise<any> {
  return cloudopsApiCall(`/guardrails/violations/${violationId}/acknowledge`, {
    method: 'PUT',
  });
}

/**
 * Resolve violation
 */
export async function resolveViolation(violationId: string, notes?: string): Promise<any> {
  return cloudopsApiCall(`/guardrails/violations/${violationId}/resolve`, {
    method: 'PUT',
    body: JSON.stringify({ notes }),
  });
}

/**
 * Update editable violation fields
 */
export async function updateGuardrailsViolation(violationId: string, update: GuardrailsViolationPatch): Promise<any> {
  return cloudopsApiCall(`/guardrails/violations/${violationId}`, {
    method: 'PATCH',
    body: JSON.stringify(update),
  });
}

/**
 * Assign or clear a guardrails violation project
 */
export async function assignGuardrailsViolationProject(violationId: string, projectId: string | null): Promise<any> {
  return updateGuardrailsViolation(violationId, { project_id: projectId });
}

/**
 * Assign or clear a guardrails violation workload
 */
export async function assignGuardrailsViolationWorkload(violationId: string, workloadId: string | null): Promise<any> {
  return cloudopsApiCall(`/guardrails/violations/${violationId}/workload`, {
    method: 'PATCH',
    body: JSON.stringify({ workload_id: workloadId }),
  });
}

/**
 * Trigger remediation for violation
 */
export async function remediateViolation(violationId: string, remediationAction: string, notes?: string): Promise<any> {
  return cloudopsApiCall(`/guardrails/violations/${violationId}/remediate`, {
    method: 'POST',
    body: JSON.stringify({
      remediation_action: remediationAction,
      notes,
    }),
  });
}

// Compliance

/**
 * Get compliance statistics for an environment
 * Note: This endpoint may not exist in the new API, keeping for backward compatibility
 */
export async function getComplianceStats(environmentId: string): Promise<any> {
  return cloudopsApiCall(`/guardrails/environments/${environmentId}/compliance`).catch(() => {
    // Fallback: return null if endpoint doesn't exist
    return null;
  });
}

// Scans

/**
 * List scans for an environment
 * Note: This endpoint may not exist in the new API, keeping for backward compatibility
 */
export async function getGuardrailsScans(environmentId: string): Promise<any> {
  return cloudopsApiCall(`/guardrails/environments/${environmentId}/scans`).catch(() => {
    // Fallback: return empty array if endpoint doesn't exist
    return [];
  });
}

/**
 * Get scan details
 */
export async function getGuardrailsScan(scanId: string): Promise<any> {
  const params = new URLSearchParams();
  const effectiveClientId = getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/guardrails/scans/${scanId}${queryString ? '?' + queryString : ''}`);
}

/**
 * Get scan details timeline (includes resources and guardrails applied)
 */
export async function getGuardrailsScanDetails(scanId: string): Promise<any> {
  const params = new URLSearchParams();
  const effectiveClientId = getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/guardrails/scans/${scanId}/details${queryString ? '?' + queryString : ''}`);
}

/**
 * Get evaluation details for a specific resource and guardrail combination
 */
export async function getGuardrailEvaluationDetails(scanId: string, resourceId: string, guardrailId: string): Promise<any> {
  const params = new URLSearchParams();
  const effectiveClientId = getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(
    `/guardrails/scans/${scanId}/details/${resourceId}/${guardrailId}/evaluation${queryString ? '?' + queryString : ''}`
  );
}

/**
 * ============================================================================
 * COST OPTIMIZATION API METHODS
 * ============================================================================
 */

/**
 * Get cost optimization recommendations for an environment
 */
export async function getCostOptimizationRecommendations(tenantId: string, filters: CostOptimizationFilters = {}): Promise<any> {
  const params = new URLSearchParams();
  if (filters.status) params.append('status_filter', filters.status);
  if (filters.type) params.append('type_filter', filters.type);
  const queryString = params.toString();
  return cloudopsApiCall(`/cost-optimization/recommendations/${tenantId}${queryString ? '?' + queryString : ''}`);
}

/**
 * Update recommendation status (Implement or Dismiss)
 */
export async function updateRecommendationStatus(recommendationId: string, status: string): Promise<any> {
  return updateRecommendation(recommendationId, { status });
}

/**
 * Update editable recommendation fields
 */
export async function updateRecommendation(recommendationId: string, update: RecommendationPatch): Promise<any> {
  return cloudopsApiCall(`/cost-optimization/recommendations/${recommendationId}`, {
    method: 'PATCH',
    body: JSON.stringify(update),
  });
}

/**
 * Assign or clear a recommendation project
 */
export async function assignRecommendationProject(recommendationId: string, projectId: string | null): Promise<any> {
  return updateRecommendation(recommendationId, { project_id: projectId });
}

/**
 * Assign or clear a recommendation workload
 */
export async function assignRecommendationWorkload(recommendationId: string, workloadId: string | null): Promise<any> {
  return cloudopsApiCall(`/cost-optimization/recommendations/${recommendationId}/workload`, {
    method: 'PATCH',
    body: JSON.stringify({ workload_id: workloadId }),
  });
}

/**
 * Trigger cost optimization scan for a client's AWS environments
 */
export async function triggerCostOptimizationScan(clientId: string): Promise<any> {
  return cloudopsApiCall(`/cost-optimization/trigger-scan/${clientId}`, {
    method: 'POST',
  });
}

/**
 * Get compute optimization statistics for dashboard
 */
export async function getComputeOptimizationStats(tenantId: string): Promise<any> {
  return cloudopsApiCall(`/cost-optimization/compute-optimization/stats/${tenantId}`);
}

/**
 * Get savings breakdown by service (EC2, ECS, EKS, RDS, SageMaker)
 */
export async function getSavingsByService(tenantId: string): Promise<any> {
  return cloudopsApiCall(`/cost-optimization/compute-optimization/savings-by-service/${tenantId}`);
}

/**
 * Get account-level cost analysis summary for an environment
 */
export async function getCostAnalysisSummary(environmentId: string): Promise<any> {
  return cloudopsApiCall(`/cost-optimization/analysis-summary/${environmentId}`);
}

/**
 * Get grouped optimization recommendations
 */
export async function getGroupedRecommendations(tenantId: string, service: string | null = null): Promise<any> {
  const params = new URLSearchParams();
  if (service) params.append('service', service);
  const queryString = params.toString();
  return cloudopsApiCall(`/cost-optimization/compute-optimization/recommendations/${tenantId}${queryString ? '?' + queryString : ''}`);
}


// ============================================
// Monitoring Environments API
// ============================================

/**
 * Get monitoring overview statistics
 */
export async function getMonitoringOverview(clientId?: string): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/environments/overview${queryString ? '?' + queryString : ''}`);
}

/**
 * Get all monitoring environments with stats
 */
export async function getMonitoringEnvironments({ clientId, isActive }: MonitoringEnvironmentsParams = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  if (isActive !== undefined) params.append('is_active', String(isActive));
  const queryString = params.toString();
  return cloudopsApiCall(`/environments/environments${queryString ? '?' + queryString : ''}`);
}

/**
 * Get single monitoring environment with stats
 */
export async function getMonitoringEnvironment(environmentId: string): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/environments/environments/${environmentId}${queryString ? '?' + queryString : ''}`);
}

/**
 * Create monitoring environment
 */
export async function createMonitoringEnvironment(environmentData: Record<string, any>): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/environments/environments${queryString ? '?' + queryString : ''}`, {
    method: 'POST',
    body: JSON.stringify(environmentData),
  });
}

/**
 * Update monitoring environment
 */
export async function updateMonitoringEnvironment(environmentId: string, environmentData: Record<string, any>): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/environments/environments/${environmentId}${queryString ? '?' + queryString : ''}`, {
    method: 'PATCH',
    body: JSON.stringify(environmentData),
  });
}

/**
 * Delete monitoring environment
 */
export async function deleteMonitoringEnvironment(environmentId: string): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/environments/environments/${environmentId}${queryString ? '?' + queryString : ''}`, {
    method: 'DELETE',
  });
}

/**
 * Trigger manual health check
 */
export async function triggerHealthCheck(environmentId: string): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/environments/environments/${environmentId}/check${queryString ? '?' + queryString : ''}`, {
    method: 'POST',
  });
}

/**
 * Get health check history
 */
export async function getHealthCheckHistory(environmentId: string, days: number = 7, limit: number = 100): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  params.append('days', String(days));
  params.append('limit', String(limit));
  return cloudopsApiCall(`/environments/environments/${environmentId}/health-checks?${params}`);
}

/**
 * Get uptime trends for charts
 */
export async function getUptimeTrends({ clientId, days = 30 }: UptimeTrendsParams = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = clientId || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  params.append('days', String(days));
  return cloudopsApiCall(`/environments/uptime-trends?${params}`);
}

/**
 * Run health checks for all due environments (staff only)
 */
export async function runAllHealthChecks(): Promise<any> {
  return cloudopsApiCall('/environments/run-health-checks', {
    method: 'POST',
  });
}

// ============================================================================
// Manual Credits API
// ============================================================================

/**
 * Get manual credits list for a client
 * @param {Object} params - Query parameters
 * @param {string} params.client_id - Client ID
 * @param {string} params.aws_account_id - Optional AWS account filter
 * @param {boolean} params.is_active - Optional active status filter
 * @param {boolean} params.include_expired - Include expired credits
 */
export async function getManualCredits({ client_id, aws_account_id, is_active, include_expired = false }: ManualCreditsParams): Promise<any> {
  const params = new URLSearchParams();
  params.append('client_id', client_id);
  if (aws_account_id) params.append('aws_account_id', aws_account_id);
  if (is_active !== undefined) params.append('is_active', String(is_active));
  params.append('include_expired', String(include_expired));

  return cloudopsApiCall(`/finops/manual-credits?${params}`);
}

/**
 * Get manual credits summary for a client
 * @param {string} client_id - Client ID
 * @param {string} aws_account_id - Optional AWS account filter
 */
export async function getManualCreditsSummary(client_id: string, aws_account_id: string | null = null): Promise<any> {
  const params = new URLSearchParams();
  if (aws_account_id) params.append('aws_account_id', aws_account_id);

  return cloudopsApiCall(`/finops/manual-credits/summary/${client_id}?${params}`);
}

/**
 * Create a new manual credit
 * @param {Object} creditData - Credit data
 * @param {string} client_id - Client ID
 */
export async function createManualCredit(creditData: Record<string, any>, client_id: string): Promise<any> {
  const params = new URLSearchParams();
  params.append('client_id', client_id);

  return cloudopsApiCall(`/finops/manual-credits?${params}`, {
    method: 'POST',
    body: JSON.stringify(creditData),
  });
}

/**
 * Update a manual credit
 * @param {string} creditId - Credit ID
 * @param {Object} updateData - Updated credit data
 * @param {string} client_id - Client ID
 */
export async function updateManualCredit(creditId: string, updateData: Record<string, any>, client_id: string): Promise<any> {
  const params = new URLSearchParams();
  params.append('client_id', client_id);

  return cloudopsApiCall(`/finops/manual-credits/${creditId}?${params}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  });
}

/**
 * Delete a manual credit
 * @param {string} creditId - Credit ID
 * @param {string} client_id - Client ID
 */
export async function deleteManualCredit(creditId: string, client_id: string): Promise<any> {
  const params = new URLSearchParams();
  params.append('client_id', client_id);

  return cloudopsApiCall(`/finops/manual-credits/${creditId}?${params}`, {
    method: 'DELETE',
  });
}

/**
 * Get platform overview metrics for dashboard widgets
 * @param {Object} params - Query parameters
 * @param {string} params.client_id - Optional client ID filter
 * @returns {Promise<Object>} Platform overview with health, compliance, environments, violations, and savings
 */
export async function getPlatformOverview({ client_id }: PlatformOverviewParams = {}): Promise<any> {
  const params = new URLSearchParams();
  // For partner/aws users, backend requires client_id as a query param.
  const effectiveClientId = client_id || getSelectedClientId();
  if (effectiveClientId) params.append('client_id', effectiveClientId);
  const queryString = params.toString();
  return cloudopsApiCall(`/overview/dashboard${queryString ? '?' + queryString : ''}`);
}
